import { formatDate } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
  NgZone,
  ChangeDetectionStrategy
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { EChartOption } from 'echarts/lib/echarts';
import { Subject } from 'rxjs';
import { ECharts } from "echarts";
import { takeUntil } from 'rxjs/operators';
import { CoreEnum } from 'src/app/enums/core.enum';
import { StatsChartMapper } from 'src/app/mapper/table/table-stats-chart.mapper';
import { GlobalUtil } from 'src/app/services/utils/global.util';
import { GraphYAxisPositionEnum } from 'src/app/tinea-components/chart/enum/y-axis-position.enum';
import { BaseChartComponent } from 'src/app/tinea-components/chart/models/BaseChartComponent';
import { TableModel } from 'src/app/tinea-components/table/models/input/table-model';
import { ConfiguredGraph, DataGroupedByAxis, GraphConfigurationReferenceLine, GraphConfigurationTag, GraphConfigurationYAxis, TagInfos, YAxisPosition } from 'src/swagger-gen/web-secured/models';
import { ModalColorTagGraphComponent } from '../../modal-color-tag-graph/modal-color-tag-graph.component';
import { MatDialog } from '@angular/material/dialog';
import { Station } from 'src/swagger-gen/web-secured/models';
import { StationsRepo } from 'src/app/repositories/stations/repo/station-repo';
import { NGXLogger } from 'ngx-logger';

@Component({
    selector: 'app-graph-echart',
    templateUrl: './graph-echart.component.html',
    styleUrls: ['./graph-echart.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GraphEchartComponent
  extends BaseChartComponent
  implements OnInit, OnDestroy {
    @Input() paramStartDate: string;
    @Input() paramEndDate: string;
    @Input() thresholdValue: Subject<boolean>;
    @Input() echartData: Subject<any>;
    @Input() isRequesting: boolean;
    @Input() selectedTagsUid: Array<any>;
    @Input() selectedTags: Array<GraphConfigurationTag>;
    @Input() customLegend: boolean;
    @Input() station: Station;
    @Input() updateTagsAxes: boolean = false;
    @Output() currentUnitsSubj = new EventEmitter<any>();
    @Output() displayStatsFromParent = new EventEmitter<any>();
    @Output() getDataFromSelection = new EventEmitter<any>();
    displayNoData: boolean;
    options: EChartOption;
    currentUnits: Array<string> = [];
    destroyed$ = new Subject<void>();
  
    yAxis: Array<GraphConfigurationYAxis> = [];
    referenceLines: Array<GraphConfigurationReferenceLine>;
  
    yAxisLeftOffset = 0;
    yAxisRightOffset = 20;
  
    readonly maxUnits = CoreEnum.MAX_UNITS;
    readonly Y_POSITION = GraphYAxisPositionEnum.LEFT;
  
    data: any;
    threshold: boolean;
    eTitle: string;
    noSelectedTag: boolean;
    echartsInstance : ECharts
    https = window.location.hostname === 'localhost' ? 'http://' : 'https://';
    domain: string =  this.https + window.location.host;
    startZoomValue = 0;
    endZoomValue = 100;
    table: TableModel;
    xTraceModeEnable = false;
    gridModeEnable = false;
    statsModeEnable = false;
    
    uidGraph: string; 
    constructor(
      private translate: TranslateService,
      private globalUtil: GlobalUtil,
      private zone: NgZone,
      private tableStatsMapper: StatsChartMapper,
      public dialog: MatDialog,
      private stationRepo : StationsRepo,
      private logger: NGXLogger,
    ) {
      super();
    }
  
    ngOnInit(): void {
      this.noSelectedTag = true;
      const localStation = JSON.parse(
        sessionStorage.getItem(CoreEnum.STATION_DETAIL)
      );
      if(localStation){
        this.uidGraph = localStation.uid;
      }
      this.echartData.pipe(takeUntil(this.destroyed$)).subscribe((val) => {
        this.paramStartDate = val.paramStartDate;
        this.paramEndDate = val.paramEndDate;
        if(val.yAxis) {
          this.yAxis = val.yAxis;
        }
        this.referenceLines = val.referenceLines;
        this.data = val.data;
        this.eTitle = val.title;
        this.setOptions(this.data);
        if(this.customLegend) {
          this.createTableMapping();
        }
      });
      this.thresholdValue.pipe(takeUntil(this.destroyed$)).subscribe((val) => {
        if (this.data) {
          this.threshold = val;
          this.setOptions(this.data);
        }
      });
    }
  
    createTableMapping() {
      const configuredGraph: ConfiguredGraph = {
        configuration: {
          configurationYAxis: this.yAxis,
          tags: this.selectedTags,
        }
      }
      const table: TableModel = this.tableStatsMapper.init(configuredGraph, this.data);
      this.table = table;
    }
  
    /*************** BUILD CHART *******************/
    setOptions(response: Array<DataGroupedByAxis>): any {
      if (response.length === 0) {
        this.currentUnitsSubj.next([]);
        this.noSelectedTag = true;
        return;
      } else {
        this.noSelectedTag = false;
      }
      let rightMargin = "230"
      if(this.yAxis.length > 3) {
        rightMargin  = "18%"
      }
      const slider = this.slider('horizontal', 'none');
      this.options = {
        tooltip: {},
        grid: [
          { left: '230', right: rightMargin, top: '10%', width: 'auto', height: '72%' },
        ],
        legend: {
          type: 'scroll',
          data: this.getLegend(response),
          orient: 'vertical',
          left: -2,
          // @ts-ignore @types/echart not updated to Echart last version (5.1.1)
          width: '15%',
          textStyle: {
            width: 100,
            // @ts-ignore @types/echart not updated to Echart last version (5.1.1)
            overflow: 'break',
            lineHeight: 16,
          },
          itemStyle: {
            opacity: 0,
          },
          top: '2%',
          bottom: 10,
          tooltip: {
          }
        },
        series: [],
        xAxis: [{
          data: response[this.getIndexMaxXAxisValues(response)].values
            .xAxisValues,
          axisLabel: {
            rotate: 30,
            formatter: (value: string) =>
              formatDate(value, 'dd/MM HH:mm:ss', 'fr-FR'),
          },
          axisPointer: {},
          type: 'time',
          min: this.paramStartDate,
          max: this.paramEndDate,
          minorSplitLine:{
            show : false,
          } 
        }],
        toolbox: {
          right: "0",
          bottom: "0",
          feature: {
              dataZoom: {},
              restore: {},
              myXTraceFeature: {
            
              },
              myGridFeature: {
                show: true,
                title: 'Grille',
                icon: this.getMyGridFeatureIcon(),
                onclick: () => {
                    this.displayGrid();
                },
              },
              myUpdateFeature: {
  
              },
              myStatsFeature: {
  
              },
              saveAsImage: {},
  
          }
        },
      };
      if(this.updateTagsAxes) {
        this.options.toolbox['feature'].myUpdateFeature = {
          show: true,
          title: 'Modifier',
          icon: 'image://'+this.domain+'/images/icons/update.svg',
          onclick: () => {
               this.displayUpdate();
          }
        }
      }
      if(this.selectedTags) {
        this.options.toolbox['feature'].myXTraceFeature = {
          show: true,
          title: 'X Trace',
          icon: this.getMyXTraceFeatureIcon(),
          onclick: () => {
              this.displayPointerAndTooltip();
          }
        }
      }
  
      // if CustomLegend
      if(this.customLegend) {
        this.options.toolbox['feature'].myStatsFeature = {
          show: true,
          title: 'Stats',
          icon: this.getMyStatsFeatureIcon(),
          onclick: () => {
              this.displayStats();
          }
        }
      }
  
      // Display Pointer
      if(this.xTraceModeEnable) {
        this.options.tooltip = {
          trigger: 'none',
          alwaysShowContent : true,
          position: function (pt) {
            return [pt[0], 130];
          },
          formatter: (params: any) => {
            let result = "";
            params.forEach(element => {
              const tag = this.selectedTags.find((tag) => (tag.name === element.seriesName))
              if(tag) {
                result += "<div style='display:flex; align-items:center;'><div style='height: 10px;width:10px; border-radius:50px;margin-right:5px;background-color:"+ tag.color +"; font-weight: 600'></div>" + element.seriesName + "&nbsp;&nbsp;<b>" + parseFloat(element.data[1]).toFixed(2) + "</b></div>"
              } 
            });
            return result;
          },
        };
        
        this.options.xAxis[0].axisPointer = {
          snap: true,
          lineStyle: {
            color: '#173A66',
            width: 2
          },
          label: {
            formatter: (params: any) =>
              formatDate(params.value, 'dd/MM HH:mm:ss', 'fr-FR'),
              backgroundColor: '#173A66'
          },
          handle: {
            show: true,
            color: '#173A66'
          }
        };
  
      }else {
        this.options.tooltip = {
          trigger: 'item',
          formatter: (params: any) => {
            const marker = params.marker;
            const seriesName = params.seriesName;
            const time = params.data[0];
            const value = params.data[1].toLocaleString('fr-FR');
            const unit = params.data[2];
            const formattedTime: string = formatDate(
              time,
              'HH:mm:ss dd/MM',
              'fr-FR'
            );
  
            return `<div>${marker} ${seriesName}</div><div>${value} ${unit} - ${formattedTime}</div>`;
          },
          axisPointer: {
            type: 'cross',
            animation: false,
          },
        };
      }
  
      if (this.eTitle) {
        this.title(this.eTitle);
      }
  
      this.getNbUnits(response);
      this.initMultipleYAxis();
  
      // Display Grid
  
      const nbSerie = response.length;
      let nbEmpty = 0;
      response.forEach((backendData) => {
        if (
          backendData.values.yAxisValues.length === 0 ||
          backendData.values.xAxisValues.length === 0
        ) {
          nbEmpty++;
        } else {
          this.displayNoData = false;
        }
        const unit = backendData.unit ? backendData.unit : '';
        const values = backendData.values.yAxisValues.map((value, i) => {
          const convertedValue = this.globalUtil.convertNumberWithDecimal(
            value,
            4
          );
          return [backendData.values.xAxisValues[i], convertedValue, unit];
        });
        const isLiftView = backendData.values.yAxisValues.every((val) =>
          Number.isInteger(Number(val))
        );
        const name = backendData.tag;
        const YAxis = this.options.yAxis as EChartOption.YAxis[];
        const thresholds = {
          high: this.globalUtil.convertNumberWithDecimal(
            backendData.highThreshold,
            4
          ),
          highLabel: this.translate.instant('stations.graph.tag.threshold.high'),
          low: this.globalUtil.convertNumberWithDecimal(
            backendData.lowThreshold,
            4
          ),
          lowLabel: this.translate.instant('stations.graph.tag.threshold.low'),
        };
        let index: number;
        if (backendData.unit) {
          index = YAxis.findIndex((yAxis) => yAxis.id === unit);
        } else {
          index = 0;
        }
        let serie;
        if(this.selectedTags && this.selectedTags.length > 0) {
          // Insert Configured Tags
          const indexConfigured = this.selectedTags.findIndex((tag) => (tag.name == backendData.tag));
          if(indexConfigured != -1) {
            const indexAxis = this.yAxis.findIndex((yAx) => (this.selectedTags[indexConfigured].yAxis === yAx.index));
             serie = this.initSerieLine(
              values,
              indexAxis,
              isLiftView,
              this.threshold ? thresholds : null,
              unit,
              name,
              this.selectedTags[indexConfigured].color,
              this.selectedTags[indexConfigured].width,
              this.selectedTags[indexConfigured].type.toLocaleLowerCase(),
            );
            this.addSerie(serie);
          }
          // Insert Reference Lines 
          if(this.referenceLines) {
            if(this.referenceLines.length > 0 ) {
              const nbData = backendData.values.yAxisValues.length;
              
              this.referenceLines.forEach((refLine) => {
                const indexAxis = this.yAxis.findIndex((yAx) => (refLine.yAxis === yAx.index));
                const nameRefLine = refLine.name
                const valuesRefLine = backendData.values.yAxisValues.map((value, i) => {
                  const convertedValue = this.globalUtil.convertNumberWithDecimal(
                    refLine.value.toString(),
                    4
                  );
                  return [backendData.values.xAxisValues[i], convertedValue, null];
                });
                serie = this.initSerieLine(
                  valuesRefLine,
                  indexAxis,
                  isLiftView,
                  this.threshold ? thresholds : null,
                  unit,
                  nameRefLine,
                  refLine.color,
                  refLine.width,
                  refLine.type.toLocaleLowerCase(),
                  true
                );
                this.addSerie(serie);
              });  
            }
          }
        } else {
          // Insert normal tag (general graph)
          serie = this.initSerieLine(
            values,
            index,
            isLiftView,
            this.threshold ? thresholds : null,
            unit,
            name,
  
          );
          this.addSerie(serie);
        }
      });
  
      if (nbSerie === nbEmpty) {
        this.displayNoData = true;
      }
    }
  
    getMyXTraceFeatureIcon() {
      if(this.xTraceModeEnable) {
        return 'image://'+this.domain+'/images/icons/x-square-enable.svg'
      } else {
        return 'image://'+this.domain+'/images/icons/x-square.svg'
      }
    }
  
    getMyGridFeatureIcon() {
      if(this.gridModeEnable) {
        return 'image://'+this.domain+'/images/icons/grid-enable.svg'
      } else {
        return 'image://'+this.domain+'/images/icons/grid.svg'
      }
    }
  
    getMyStatsFeatureIcon() {
      if(this.statsModeEnable) {
        return 'image://'+this.domain+'/images/icons/stats-enable.svg'
      } else {
        return 'image://'+this.domain+'/images/icons/stats.svg'
      }
    }

    displayUpdate() {
      this.zone.run(() => {
        this.openModalColorTag();
      });
    }
    displayGrid() {
      this.zone.run(() => {
        this.gridModeEnable = !this.gridModeEnable;
        this.onInitGrid()
      });
    }
    displayStats() {
      this.zone.run(() => {
      this.statsModeEnable = !this.statsModeEnable;
      this.displayStatsFromParent.next(this.statsModeEnable);
      this.options.toolbox["feature"].myStatsFeature = {
        icon: this.getMyStatsFeatureIcon(),
      }
      this.echartsInstance.setOption(this.options);
      });
    }

  
  
    displayPointerAndTooltip() {
      this.zone.run(() => {
      this.xTraceModeEnable = !this.xTraceModeEnable;
      this.setOptions(this.data);
      });
    }
  
    getIndexMaxXAxisValues(response: Array<DataGroupedByAxis>): number {
      let xdefault = 0;
      let index = 0;
      response.forEach((backendData, i) => {
        const nbX = backendData.values.xAxisValues.length;
        if (nbX > xdefault) {
          xdefault = nbX;
          index = i;
        }
      });
      return index;
    }
  
    getNbUnits(response: Array<DataGroupedByAxis>) {
      this.currentUnits = [];
      response.forEach((backendData) => {
        const unit = backendData.unit ? backendData.unit : '';
        if (
          this.currentUnits.indexOf(unit) === -1 &&
          this.currentUnits.length < this.maxUnits
        ) {
          this.currentUnits.push(unit);
          this.currentUnitsSubj.next(this.currentUnits);
        }
      });
    }
  
    initMultipleYAxis() {
      this.yAxisLeftOffset = 0;
      this.yAxisRightOffset = 20;
      const yAxisContainer: EChartOption.YAxis[] = [];
      if(this.yAxis && this.yAxis.length > 0) {
        this.yAxis.forEach((yAxi, index) => {
          let position;
          if(yAxi.position === YAxisPosition.LEFT) {
            position = "left";
          } else {
            position = "right";
          }
          const yAxisObject: EChartOption.YAxis = {
            type: 'value',
            id: yAxi.name + index,
            name: yAxi.name,
            position: position,
            axisLabel: {
              formatter: (value) => value,
            },
            axisLine: {
              show: true,
              lineStyle: {
                color: yAxi.color
              },
              onZero: false,
            },
            axisPointer: {
              show: true,
              label: {
                formatter: (params: any) =>
                  this.globalUtil
                    .convertNumberWithDecimal(params.value, 4)
                    .toLocaleString('fr-FR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 4,
                    })
              },
            },
            minorSplitLine:{
              show : false,
            } 
          };
  
          if(yAxi.position === YAxisPosition.LEFT) {
            yAxisObject.offset = this.yAxisLeftOffset;
            this.yAxisLeftOffset = this.yAxisLeftOffset + 80;
          } else {
            yAxisObject.offset = this.yAxisRightOffset;
            this.yAxisRightOffset = this.yAxisRightOffset + 80;
            // Masque les lignes horizontales tout au long de l'axe
            yAxisObject.splitLine = {
              show: false
            }
          }
  
          if(yAxi.min || yAxi.min === 0) {
            yAxisObject.min = yAxi.min
          }
  
          if(yAxi.max || yAxi.max === 0) {
            yAxisObject.max = yAxi.max
          }     
  
          yAxisContainer.push(yAxisObject);
        });
  
      } else {
        this.currentUnits.forEach((unit, index) => {
          const yAxisObject: EChartOption.YAxis = {
            type: 'value',
            id: unit,
            axisLabel: {
              formatter: (value) => value + ' ' + unit,
            },
            axisLine: {
              show: true,
              lineStyle: {
                color: "#5e5e5e"
              },
              onZero: false
            },
            axisPointer: {
              show: true,
              label: {
                formatter: (params: any) =>
                  this.globalUtil
                    .convertNumberWithDecimal(params.value, 4)
                    .toLocaleString('fr-FR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 4,
                    }) +
                  ' ' +
                  unit,
              },
            },
            minInterval: 1,
            min: function (value) {
              let v: number;
              const min = value.min;
              const max = value.max;
              const delta = (max - min) * 0.1;
              if ((min > 0 && min - delta > 0) || min < 0) {
                v = min - delta;
              } else {
                v = 0;
              }
              return Math.floor(v);
            },
          };
          if (yAxisContainer.length < this.maxUnits) {
            if (unit === '') {
              yAxisContainer.unshift(yAxisObject);
            } else {
              yAxisContainer.push(yAxisObject);
            }
          }
        });
      }
      
      this.options.yAxis = yAxisContainer;
    }
  
    getLegend(response: Array<DataGroupedByAxis>): Array<string> {
      const arrLegends = [];
      response.forEach((item) => {
        arrLegends.push(item.tag);
      });
      return arrLegends;
    }
  
    ngOnDestroy() {
      this.destroyed$.next();
      this.destroyed$.complete();
    }

    openModalColorTag() {
      const dialogRef = this.dialog.open(ModalColorTagGraphComponent, {
        data: {tag: null, yAxis: this.yAxis, tags : this.selectedTags},
        disableClose: true,
        width: "600px",
        height: "800px",
      });
      if (dialogRef) {
        dialogRef.afterClosed().subscribe((result: any) => {
          if(result.yAxis) {
            for(let i = 0; i < this.yAxis.length; i++){
              if(this.yAxis[i].index == result.yAxis.index) {
                this.yAxis[i] = result.yAxis
              }
            }
          }
          if(result.configuredTag) {
            for(let i = 0; i < this.selectedTags.length; i++){
              if(this.selectedTags[i].uid == result.configuredTag.uid) {
                this.selectedTags[i] = result.configuredTag
              }
            }
          }
          this.getDataFromSelection.next({
            list: this.selectedTagsUid,
            listConfigured: this.selectedTags,
            yAxis: this.yAxis,
            referenceLines: this.referenceLines,
            station: this.station,
          });
        });
      }
      
    }
    onInitCharts(ec) {
      this.echartsInstance = ec;
      this.echartsInstance.setOption(this.options);
    }
    onInitGrid() {
      this.options.xAxis[0].minorSplitLine.show = this.gridModeEnable;
      this.options.yAxis[0].minorSplitLine.show = this.gridModeEnable;
      this.options.toolbox["feature"].myGridFeature = {
        icon: this.getMyGridFeatureIcon(),
      }
      this.echartsInstance.setOption(this.options);
      
    }
    onZoom(event){
      const startTimestamp = event.batch[0].startValue ? event.batch[0].startValue :  event.batch[0].start;
      const endTimestamp = event.batch[0].endValue ? event.batch[0].endValue :  event.batch[0].end;
      // je fais cette vérification car quand on revient au début avec le resetZoom on a la valeur 'start' est à 0
      if(startTimestamp != 0) {

    
      let startDate = new Date(startTimestamp);
      let endDate = new Date(endTimestamp);
      const startFormatted = startDate.toISOString().slice(0, 19) + 'Z';
      const endFormatted = endDate.toISOString().slice(0, 19) + 'Z';
      
      this.stationRepo
      .getStatsTags(this.uidGraph,this.selectedTagsUid,startFormatted, endFormatted)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res: any) => {
          const datas = res['body'];
          const dataRow = this.table.dataRow;         
          for(var i = 0; i < datas.length; i++) {
            for(var j = 0; j < dataRow.length; j++) {
              if(datas[i].uid == dataRow[j].row.obj.uid){
                dataRow[j].cells[2].valueCells[0].displayedValue = parseFloat(datas[i].stats['minValue']).toFixed(2);
                dataRow[j].cells[3].valueCells[0].displayedValue = parseFloat(datas[i].stats['averageValue']).toFixed(2);
                dataRow[j].cells[4].valueCells[0].displayedValue = parseFloat(datas[i].stats['maxValue']).toFixed(2);
              }
            }
          }
        },
        error: (err) => {
          this.logger.log(
            'An error has occured during retrieve stats graph data',
            err
          );
        }
      });
    }else{
      this.createTableMapping();
    }
    }
      
  onRestore($event){
   this.createTableMapping();
  }
}
